/**
 * @graphql agg
 */
import { useCallback, useContext, useMemo } from 'react';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl-next';
import { useApolloClient } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import type {
	ConfluenceGraphQLSearchQueryType,
	ConfluenceGraphQLSearchVariables,
} from '@atlassian/search-client';
import {
	confluenceGraphQLSearchVariablesMapper,
	ConfluenceQuickSearchQuery,
} from '@atlassian/search-client';
import {
	type SearchResultSection,
	type SearchResult,
	type PostQuerySupplierArgs,
	useExperiments,
} from '@atlassian/product-search-dialog';
import type { ProductEdition, SearchExperimentLayer } from '@atlassian/search-experiment';
import { PlatformSiteAri } from '@atlassian/ari';

import { fg } from '@confluence/feature-gating';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { QUICK_SEARCH_PRE_QUERY } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { useDynamicBackendExperiment } from '../useDynamicBackendExperiment.ts';

import { GenericIconRenderer } from './IconRenderer.tsx';
import { ConfluenceResultRenderer } from './ResultRenderer.tsx';
import { RecentQuickSearchQuery } from './RecentQuickSearchQuery.graphql.ts';
import { i18n } from './messages.ts';
import type {
	RecentQuickSearchQuery as RecentQuickSearchType,
	RecentQuickSearchQuery_activities_myActivities_workedOn_edges as RecentQuickSearchActivityEdgeType,
	RecentQuickSearchQuery_myVisitedSpaces_nodes_spaces as RecentQuickSearchSpaceType,
	RecentQuickSearchQueryVariables,
	ActivitiesObjectType,
} from './__types__/RecentQuickSearchQuery.ts';
import type {
	useConfluenceItemSuppliersRecentQuickSearchQuery_activity_Activity_myActivity_MyActivity_all_ActivityConnection_edges_ActivityItemEdge as ActivityEdgeV3Type,
	ConfluenceContentStatus,
	ConfluencePageStatus,
	ConfluenceBlogPostStatus,
} from './__types__/useConfluenceItemSuppliersRecentQuickSearchQuery.ts';

type ConfluenceActivityData = Extract<
	NonNullable<ActivityEdgeV3Type['node']['object']['data']>,
	{ id: string }
> & {
	status?: ConfluenceContentStatus | null;
	pageStatus?: ConfluencePageStatus | null;
	blogStatus?: ConfluenceBlogPostStatus | null;
};
import { ConfluenceTitleRenderer } from './TitleRenderer.tsx';

const RECENT_CONTENTS_FETCH_LIMIT = 100;
const RECENT_SPACE_FETCH_LIMIT = 10;
const MAX_RESULTS_CONTENTS = 10;
const MAX_RESULTS_SPACES = 3;

const recentContentsCCResultMapper: (
	edge: RecentQuickSearchActivityEdgeType | null,
) => SearchResult = (edge) => {
	const item = edge?.node?.object?.content;
	return {
		id: item?.id || '',
		title: item?.title || '',
		type: item?.type || '',
		url: `${CONTEXT_PATH}${item?.links?.webui || ''}`,
		meta: item?.space?.name || '',
		additionalMeta: item?.type || '',
		containerId: item?.space?.id || '',
		icon: item?.type || '',
		isRecentResult: true,
	};
};

const recentContentsCCResultMapperV3 = (edge: ActivityEdgeV3Type): SearchResult => {
	const item = edge?.node?.object?.data as ConfluenceActivityData | null;
	const normalizedType =
		item?.type?.toLowerCase() === 'blog_post' ? 'blogpost' : item?.type?.toLowerCase();

	return {
		id: item?.id || '',
		title: item?.title || '',
		type: normalizedType,
		url: `${CONTEXT_PATH}${item?.links?.webUi || ''}`,
		meta: item?.space?.name || '',
		additionalMeta: normalizedType,
		containerId: item?.space?.id || '',
		icon: normalizedType,
		isRecentResult: true,
	};
};

const recentSpaceCCResultMapper: (item: RecentQuickSearchSpaceType | null) => SearchResult = (
	item,
) => ({
	id: item?.id || '',
	title: item?.name || '',
	type: 'space',
	meta: '',
	url: `${CONTEXT_PATH}${item?.links?.webui || ''}`,
	containerId: item?.id || undefined,
	icon: GenericIconRenderer({
		iconUrl: `${CONTEXT_PATH}${item?.icon?.path || ''}`,
		size: 'xsmall',
	}),
	isRecentResult: true,
});

type useConfluenceSuppliersArgs = {
	cloudId: string;
	formatDate: (date: string) => ReactElement;
	userId: string | null;
	principalId?: string;
	autocorrectEnabled?: boolean;
	isQuickSearchForWhiteboardsEnabled: boolean;
	isQuickSearchForDatabasesEnabled: boolean;
	objectTypes: ActivitiesObjectType[];
};

type useConfluenceSuppliersType = {
	preQueryItemSupplier: () => Promise<{ sections: SearchResultSection[] }>;
	postQueryItemSupplier?: (args: PostQuerySupplierArgs) => Promise<{
		sections: SearchResultSection[];
	}>;
	searchClientConfig?: {
		graphQLEnabled: true;
		graphQLPromise: (args: PostQuerySupplierArgs) => Promise<ConfluenceGraphQLSearchQueryType>;
		cloudId: string;
		userId?: string;
	};
	experimentLayers?: SearchExperimentLayer[];
	productEdition?: ProductEdition;
};

export const useConfluenceItemSuppliers = ({
	cloudId,
	userId,
	principalId = 'context',
	autocorrectEnabled = false,
	isQuickSearchForWhiteboardsEnabled,
	isQuickSearchForDatabasesEnabled,
	objectTypes,
}: useConfluenceSuppliersArgs): useConfluenceSuppliersType => {
	const intl = useIntl();
	const confCloudClient = useApolloClient();
	const aggClient = getAGGClient();
	const workedOnAPIEnabled = false;

	const experienceTracker = useContext(ExperienceTrackerContext);

	const contentEntities = useMemo(
		() => [
			'ati:cloud:confluence:page',
			'ati:cloud:confluence:blogpost',
			'ati:cloud:confluence:attachment',
			'ati:cloud:confluence:embed',
			'ati:cloud:confluence:folder',
			...(isQuickSearchForWhiteboardsEnabled ? ['ati:cloud:confluence:whiteboard'] : []),
			...(isQuickSearchForDatabasesEnabled ? ['ati:cloud:confluence:database'] : []),
		],
		[isQuickSearchForWhiteboardsEnabled, isQuickSearchForDatabasesEnabled],
	);

	const preQueryItemSupplier = useCallback(async (): Promise<{
		sections: SearchResultSection[];
	}> => {
		experienceTracker.start({
			name: QUICK_SEARCH_PRE_QUERY,
		});

		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const {
			recentContents,
			recentSpaces,
		}: { recentContents: SearchResult[]; recentSpaces: SearchResult[] } = !userId
			? {
					recentContents: [],
					recentSpaces: [],
				}
			: expValEquals('cc_migrate_remaining_activity_v2_queries_to_v3', 'isEnabled', true)
				? await confCloudClient
						.query({
							query: RecentQuickSearchQueryV3,
							variables: {
								cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
								contentItemsLimit: RECENT_CONTENTS_FETCH_LIMIT,
								spaceLimit: RECENT_SPACE_FETCH_LIMIT,
								objectTypes: objectTypes.map(String),
								eventTypes: workedOnAPIEnabled ? ['CREATED', 'EDITED', 'VIEWED'] : ['VIEWED'],
							},
						})
						.then((response) => {
							const rawSpaces = response.data?.myVisitedSpaces?.nodes?.spaces;
							const rawContent = response.data?.activity?.myActivity?.all?.edges;
							const filteredRawContent =
								rawContent &&
								rawContent.filter(
									// #help-work-platform suggested handling null content in FE while they investigate why some results are being returned null
									(edge: ActivityEdgeV3Type) => {
										const data = edge?.node?.object?.data as ConfluenceActivityData | null;
										return (
											!!data &&
											data.status !== 'TRASHED' &&
											data.pageStatus !== 'TRASHED' &&
											data.blogStatus !== 'TRASHED'
										);
									},
								);
							return {
								recentContents: filteredRawContent?.map(recentContentsCCResultMapperV3) || [],
								recentSpaces: rawSpaces?.map(recentSpaceCCResultMapper) || [],
							};
						})
						.catch((error) => {
							experienceTracker.fail({
								name: QUICK_SEARCH_PRE_QUERY,
								error,
							});

							// error is handled by the platform PSD component
							markErrorAsHandled(error);

							// The error will bubble up and the parent component will catch the error and handle it
							throw error;
						})
				: await confCloudClient
						.query<RecentQuickSearchType, RecentQuickSearchQueryVariables>({
							query: RecentQuickSearchQuery,
							variables: {
								cloudId,
								contentItemsLimit: RECENT_CONTENTS_FETCH_LIMIT,
								spaceLimit: RECENT_SPACE_FETCH_LIMIT,
								objectTypes,
								workedOnAPIEnabled,
							},
						})
						.then((response) => {
							const rawSpaces = response.data?.myVisitedSpaces?.nodes?.spaces;
							const rawContent = workedOnAPIEnabled
								? response.data.activities?.myActivities?.workedOn?.edges
								: response.data.activities?.myActivities?.viewed?.edges;
							const filteredRawContent =
								rawContent &&
								rawContent.filter(
									// #help-work-platform suggested handling null content in FE while they investigate why some results are being returned null
									(edge) =>
										!(edge?.node?.object?.content?.status === 'trashed') &&
										!(edge?.node?.object?.content === null),
								);
							return {
								recentContents: filteredRawContent?.map(recentContentsCCResultMapper) || [],
								recentSpaces: rawSpaces?.map(recentSpaceCCResultMapper) || [],
							};
						})
						.catch((error) => {
							experienceTracker.fail({
								name: QUICK_SEARCH_PRE_QUERY,
								error,
							});

							// error is handled by the platform PSD component
							markErrorAsHandled(error);

							// The error will bubble up and the parent component will catch the error and handle it
							throw error;
						});

		experienceTracker.succeed({
			name: QUICK_SEARCH_PRE_QUERY,
		});

		return {
			sections: [
				{
					id: 'recent-confluence-items',
					title: workedOnAPIEnabled
						? intl.formatMessage(i18n.recentlyWorkedOn)
						: intl.formatMessage(i18n.recentlyViewed),
					searchResults: recentContents,
					resultLimit: MAX_RESULTS_CONTENTS,
					titleRenderer: ConfluenceTitleRenderer(
						autocorrectEnabled,
						workedOnAPIEnabled,
						intl.formatMessage,
					),
					resultRenderer: ConfluenceResultRenderer,
				},
				{
					id: 'recent-confluence-spaces',
					title: intl.formatMessage(i18n.spaces),
					searchResults: recentSpaces,
					resultLimit: MAX_RESULTS_SPACES,
				},
			],
		};
	}, [
		confCloudClient,
		cloudId,
		userId,
		intl,
		autocorrectEnabled,
		experienceTracker,
		workedOnAPIEnabled,
		objectTypes,
	]);

	const { backendExperiment: staticBackendExperiment } = useExperiments();

	const { backendExperiment: dynamicBackendExperiment, productEdition } =
		useDynamicBackendExperiment({
			cloudId,
			entities: contentEntities,
			userId: userId || undefined,
			experience: 'quick-search',
		}) || {};

	const backendExperiment = fg('confluence_search-scalable-statsig-layers')
		? dynamicBackendExperiment
		: staticBackendExperiment;

	const { experimentId, shadowExperimentId, experimentLayers } = backendExperiment || {};

	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const searchClientConfig = {
		graphQLEnabled: true as true,
		graphQLPromise: (args: PostQuerySupplierArgs) =>
			aggClient
				.query<ConfluenceGraphQLSearchQueryType, ConfluenceGraphQLSearchVariables>({
					query: ConfluenceQuickSearchQuery,
					variables: confluenceGraphQLSearchVariablesMapper({
						...args,
						cloudId,
						contentEntities,
						experimentId,
						shadowExperimentId,
						experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
							name,
							layerId,
							shadowId,
							definitions,
						})),
						isLivePagesEnabled: fg('confluence_live_pages_open_beta_trait_opted_in'),
					}),
				})
				.then((response) => response.data),
		cloudId,
		userId: principalId,
	};

	return {
		experimentLayers: !fg('confluence_search-scalable-statsig-layers')
			? undefined
			: backendExperiment?.experimentLayers?.map((layer) => {
					const { analyticsDefinitions, ...rest } = layer;

					return {
						...rest,
						definitions: analyticsDefinitions,
					};
				}),
		productEdition,
		preQueryItemSupplier,
		searchClientConfig,
	};
};

export const RecentQuickSearchQueryV3 = gql`
	query useConfluenceItemSuppliersRecentQuickSearchQuery(
		$cloudId: ID!
		$contentItemsLimit: Int!
		$spaceLimit: Int!
		$objectTypes: [String!]
		$eventTypes: [String!]
	) {
		activity {
			myActivity {
				all(
					filter: {
						type: AND
						rootContainerIds: [$cloudId]
						arguments: {
							products: ["CONFLUENCE"]
							objectTypes: $objectTypes
							eventTypes: $eventTypes
						}
					}
					first: $contentItemsLimit
				) {
					edges {
						node {
							id
							object {
								id
								data {
									... on ConfluencePage {
										id
										title
										type
										pageStatus: status
										links {
											webUi
										}
										space {
											id
											name
										}
									}
									... on ConfluenceBlogPost {
										id
										title
										type
										blogStatus: status
										links {
											webUi
										}
										space {
											id
											name
										}
									}
									... on ConfluenceWhiteboard {
										id
										title
										type
										status
										links {
											webUi
										}
										space {
											id
											name
										}
									}
									... on ConfluenceDatabase {
										id
										title
										type
										status
										links {
											webUi
										}
										space {
											id
											name
										}
									}
									... on ConfluenceEmbed {
										id
										title
										type
										status
										links {
											webUi
										}
										space {
											id
											name
										}
									}
								}
							}
						}
					}
				}
			}
		}
		myVisitedSpaces(limit: $spaceLimit) {
			nodes {
				spaces {
					id
					key
					name
					icon {
						path
					}
					links {
						webui
					}
				}
			}
		}
	}
`;
